import * as React from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { HelpCircle } from "lucide-react";

import { cn } from "@/lib/utils";

const OPEN_HOVER_DELAY_MS = 350;
const CLOSE_HOVER_DELAY_MS = 140;

interface InfoHintProps {
  /** Popup body — plain text or rich nodes. */
  children: React.ReactNode;
  /** Accessible name for the trigger button. */
  label?: string;
  side?: React.ComponentProps<typeof PopoverPrimitive.Content>["side"];
  align?: React.ComponentProps<typeof PopoverPrimitive.Content>["align"];
  sideOffset?: number;
  /** Extra classes for the trigger button. */
  className?: string;
  /** Extra classes for the popup content. */
  contentClassName?: string;
  /** Delay before a hover opens the popup (ms). */
  hoverDelay?: number;
}

/**
 * A small "?" circle that reveals a themed help popup — reusable for inline field/section hints
 * across the platform. Opens on click (works on touch) or on delayed hover, and closes on
 * mouse-out, outside click, or Escape. Move verbose helper copy in here to keep forms uncluttered.
 */
export function InfoHint({
  children,
  label = "More information",
  side = "top",
  align = "center",
  sideOffset = 6,
  className,
  contentClassName,
  hoverDelay = OPEN_HOVER_DELAY_MS,
}: InfoHintProps) {
  const [open, setOpen] = React.useState(false);
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = React.useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  const scheduleOpen = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpen(true), hoverDelay);
  };
  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_HOVER_DELAY_MS);
  };

  // Radix drives this for its own interactions (trigger click, outside click, Escape); clearing
  // pending hover timers here keeps them from re-opening/re-closing right after a click.
  const handleOpenChange = (next: boolean) => {
    clearTimers();
    setOpen(next);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          onPointerEnter={(event) => {
            if (event.pointerType !== "touch") scheduleOpen();
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== "touch") scheduleClose();
          }}
          className={cn(
            "inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            className
          )}
        >
          <HelpCircle className="size-3.5" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerEnter={clearTimers}
          onPointerLeave={(event) => {
            if (event.pointerType !== "touch") scheduleClose();
          }}
          className={cn(
            "bg-popover text-popover-foreground z-[220] w-64 rounded-md border p-3 text-xs leading-relaxed shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            contentClassName
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
